import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import DirectPayment from "components/DirectPayment/DirectPayment";
import LoanPayment from "components/LoanPayment/LoanPayment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  SingleSelect,
  Text,
} from "widgets";
import PaymentDetails from "components/PaymentDetails/PaymentDetails";
import { useSelectedOrderDetails } from "store/backend/assets";
import DisplayOrder from "components/DisplayOrder/DisplayOrder";
import {
  INR_SYMBOL,
  SUCCESSFUL_REFUND_STATUSES,
  PAYMENT_STATUSES,
  SUCCESSFUL_PAYMENT_STATUSES,
  PART_PAYMENT,
  LEARN_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  LOAN_STATUSES,
  KH_VERTICAL_ID,
  PAYMENT_PLANS,
  PAYMENT_PLAN_OPTIONS,
} from "constants/config";
import {
  PUNCH_PAYMENT_V2_ERROR_MESSAGES,
  getApiErrorMessage,
} from "constants/apiErrorMessages";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddNote from "components/AddNote/AddNote";
import PartPayment from "components/PartPayment/PartPayment";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";

import styles from "./PaymentPreference.module.scss";

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const handleOnClickAddNote = (setOpenDialogContent) => {
  setOpenDialogContent(true);
};
const checkIfLearnEligibleForPartPayment = (
  totalAmount,
  amountPaid,
  paymentDetails,
) => {
  const isLoanCancelled = paymentDetails.some(
    (payment) =>
      payment.method?.id === LOAN_PAYMENT_METHOD &&
      payment.status.id === PAYMENT_STATUSES.REJECTED_BY_INFINITY_LEARN,
  );
  if (isLoanCancelled) {
    return false;
  }
  const paymentUptoTwentyFivePercent = totalAmount * 0.25;
  if (amountPaid >= paymentUptoTwentyFivePercent) {
    return true;
  }
  return false;
};

const hasPunchedLoan = (paymentDetails = []) => {
  if (!Array.isArray(paymentDetails)) return false;
  return paymentDetails.some((payment) => {
    if (payment?.method?.id !== LOAN_PAYMENT_METHOD) return false;
    const paymentStatusId = payment?.status?.id;
    const loanDetailsStatusId = payment?.loan_details?.status?.id;
    return (
      paymentStatusId === PAYMENT_STATUSES.CREATED ||
      paymentStatusId === PAYMENT_STATUSES.DISBURSED ||
      loanDetailsStatusId === LOAN_STATUSES.CREATED ||
      loanDetailsStatusId === LOAN_STATUSES.DISBURSED
    );
  });
};

const showSelectPaymentPlanDropdown = (isPartPaymentScheduleConfirmed) => {
  if (isPartPaymentScheduleConfirmed) {
    return false;
  }
  return true;
};

const getFirstPaymentCollection = (
  amountPaid,
  minimumCollectionAmount,
  headAmount,
  orderAmount,
) => {
  if (orderAmount <= minimumCollectionAmount) {
    return orderAmount;
  }
  if (minimumCollectionAmount && !amountPaid) {
    return minimumCollectionAmount;
  }
  if (!minimumCollectionAmount && !amountPaid) {
    return headAmount;
  }
};

const PaymentPreference = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const paymentPlanFromUrl = searchParams.get("payment_plan");
  const fromSwitchToLoan = searchParams.get("switch_to_loan") === "true";
  const salesPersonId = localStorage.getItem("sales_person_id");
  const [openDialogContent, setOpenDialogContent] = useState(null);

  const {
    isPending: isSelectedOrderDetailsLoading,
    data: selectedOrderDetails,
    isError: isErrorOnSelectedOrderDetails,
    error: errorMessageOnSelectedOrderDetails,
  } = useSelectedOrderDetails(orderId);

  const isPartPaymentScheduleConfirmed =
    selectedOrderDetails?.part_payment_details?.length > 0 || false;

  const selectedOrderVerticalId =
    selectedOrderDetails && selectedOrderDetails.business_unit_id;

  const isAnyAmountPaid =
    selectedOrderDetails && selectedOrderDetails.amount_paid > 0;

  const selectedFlowType =
    selectedOrderDetails && selectedOrderDetails.payment_flow_type?.id;
  const [paymentPlan, setPaymentPlan] = useState(PAYMENT_PLANS.FULL_PAYMENT);

  const orderDetailsAmountPaid =
    selectedOrderDetails && selectedOrderDetails.amount_paid;

  const paymentDetails =
    (selectedOrderDetails && selectedOrderDetails.payment_details) || [];
  const orderHasPunchedLoan = hasPunchedLoan(paymentDetails);

  const hasLoanIntent = selectedOrderDetails?.loan_intent != null;

  useEffect(() => {
    if (fromSwitchToLoan) {
      setPaymentPlan(PAYMENT_PLANS.LOAN);
      return;
    }
    // if (orderHasPunchedLoan) {
    //   setPaymentPlan(PAYMENT_PLANS.LOAN);
    //   return;
    // }
    if (hasLoanIntent) {
      setPaymentPlan(PAYMENT_PLANS.LOAN);
      return;
    }
    if (isPartPaymentScheduleConfirmed) {
      setPaymentPlan(PAYMENT_PLANS.PART_PAYMENT);
      return;
    }
    // const urlPlan =
    //   paymentPlanFromUrl != null ? Number(paymentPlanFromUrl) : undefined;
    // if (urlPlan !== undefined && !Number.isNaN(urlPlan)) {
    //   setPaymentPlan(urlPlan);
    //   return;
    // }
    setPaymentPlan(PAYMENT_PLANS.FULL_PAYMENT);
  }, [
    paymentPlanFromUrl,
    fromSwitchToLoan,
    isPartPaymentScheduleConfirmed,
    orderHasPunchedLoan,
    hasLoanIntent,
  ]);

  const headAmount =
    String(selectedOrderVerticalId) === String(KH_VERTICAL_ID)
      ? (() => {
          const paymentHeads =
            selectedOrderDetails?.packages?.[0]?.subscriptions?.[0]
              ?.payment_heads;

          if (!Array.isArray(paymentHeads) || paymentHeads.length === 0) {
            return undefined;
          }

          const firstHead = paymentHeads[0];
          return Number(firstHead.amount_paid) < Number(firstHead.amount)
            ? firstHead.amount
            : undefined;
        })()
      : undefined;

  const minimumCollectionAmount =
    selectedOrderDetails && selectedOrderDetails.min_collection_amount;

  const firstPaymentCollectionAmount = getFirstPaymentCollection(
    orderDetailsAmountPaid,
    minimumCollectionAmount,
    headAmount,
    selectedOrderDetails?.amount,
  );

  if (isSelectedOrderDetailsLoading) {
    return <FullPageLoader />;
  }

  if (isErrorOnSelectedOrderDetails) {
    const friendlyMessage = getApiErrorMessage(
      errorMessageOnSelectedOrderDetails,
      PUNCH_PAYMENT_V2_ERROR_MESSAGES,
    );
    const isLoanAlreadyExistsMessage =
      friendlyMessage ===
      PUNCH_PAYMENT_V2_ERROR_MESSAGES.ACTIVE_LOAN_ALREADY_EXISTS;
    return (
      <div>
        <ErrorMessage>
          {isLoanAlreadyExistsMessage
            ? "Unable to load order details. Please try again."
            : friendlyMessage}
        </ErrorMessage>
      </div>
    );
  }

  if (!selectedOrderDetails) {
    return (
      <ErrorMessage>
        Order Details not found for Order Id {orderId}
      </ErrorMessage>
    );
  }

  const totalAmount = reduceToFixedDecimals(
    selectedOrderDetails.inr_conversion_amount,
  );

  const totalSuccessfulPaymentAmount = reduceToFixedDecimals(
    selectedOrderDetails.payment_details
      .filter((payment) =>
        SUCCESSFUL_PAYMENT_STATUSES.includes(payment.status.id),
      )
      .reduce((sum, payment) => sum + payment.amount, 0),
  );
  const totalSuccessfulRefundedAmount = reduceToFixedDecimals(
    selectedOrderDetails.refund_details
      .filter((refund) => SUCCESSFUL_REFUND_STATUSES.includes(refund.status.id))
      .reduce((sum, refund) => sum + refund.refunded_amount, 0),
  );

  const pendingAmount = Math.max(
    0,
    reduceToFixedDecimals(
      totalAmount -
        totalSuccessfulPaymentAmount +
        totalSuccessfulRefundedAmount,
    ),
  );

  const paymentUnderProcessing = reduceToFixedDecimals(
    selectedOrderDetails.payment_details.length > 0
      ? selectedOrderDetails.payment_details
          .filter((payment) => payment.status.id === PAYMENT_STATUSES.CREATED)
          .reduce((sum, payment) => sum + payment.amount, 0)
      : 0,
  );

  const amountPaid = reduceToFixedDecimals(
    totalSuccessfulPaymentAmount - totalSuccessfulRefundedAmount,
  );

  const excessAmountPaid = Math.min(
    0,
    reduceToFixedDecimals(
      totalAmount -
        totalSuccessfulPaymentAmount +
        totalSuccessfulRefundedAmount,
    ),
  );

  const isLearnEligibleForPartPayment = checkIfLearnEligibleForPartPayment(
    totalAmount,
    amountPaid,
    paymentDetails,
  );

  const isLoanPaid = paymentDetails.some(
    (payment) =>
      payment.method.id === LOAN_PAYMENT_METHOD &&
      payment.status.id === PAYMENT_STATUSES.DISBURSED,
  );
  const filteredPaymentOptions = PAYMENT_PLAN_OPTIONS.filter((payment_plan) => {
    if (isLoanPaid) {
      return payment_plan.value === PAYMENT_PLANS.FULL_PAYMENT;
    }

    if (
      payment_plan.value === PAYMENT_PLANS.PART_PAYMENT &&
      selectedOrderVerticalId !== KH_VERTICAL_ID
    ) {
      return false;
    }

    return true;
  });

  const isFullAmountPaid = totalAmount <= amountPaid;

  const isPaymentPlanFrozen =
    fromSwitchToLoan && paymentPlan === PAYMENT_PLANS.LOAN;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.paymentArea}>
          {!isFullAmountPaid ? (
            <>
              <div className={styles.field}>
                <Text>Select Payment Plan</Text>
                {isPaymentPlanFrozen ? (
                  <div className={styles.paymentPlanFrozen}>Loan</div>
                ) : (
                  <SingleSelect
                    tag="Payment Plan"
                    value={Number(paymentPlan)}
                    handleChange={(event) => setPaymentPlan(event.target.value)}
                    menuItems={filteredPaymentOptions}
                  />
                )}
              </div>
              {paymentPlan === PAYMENT_PLANS.FULL_PAYMENT && (
                <DirectPayment
                  totalAmount={totalAmount}
                  payableAmount={pendingAmount}
                  orderId={orderId}
                  paymentFlowTypeId={PAYMENT_PLANS.FULL_PAYMENT}
                  salesPersonId={salesPersonId}
                  firstPaymentCollectionAmount={firstPaymentCollectionAmount}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                />
              )}
              {paymentPlan === PAYMENT_PLANS.PART_PAYMENT && (
                <PartPayment
                  selectedOrderId={orderId}
                  isPartPaymentScheduleConfirmed={
                    isPartPaymentScheduleConfirmed
                  }
                  selectedPartPaymentDetails={
                    selectedOrderDetails.part_payment_details
                  }
                  isAnyAmountPaid={isAnyAmountPaid}
                  salesPersonId={salesPersonId}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                  selectedFlowType={selectedFlowType}
                  fromSwitchToLoan={fromSwitchToLoan}
                />
              )}
              {paymentPlan === PAYMENT_PLANS.LOAN && (
                <LoanPayment
                  selectedOrderId={orderId}
                  pendingAmount={pendingAmount}
                  totalAmount={totalAmount}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                  salesPersonId={salesPersonId}
                  paymentDetails={paymentDetails}
                  loanIntent={selectedOrderDetails?.loan_intent}
                  orderAmountPaid={amountPaid}
                  fromSwitchToLoan={fromSwitchToLoan}
                />
              )}
            </>
          ) : (
            <div className={styles.successfulPaymentMessage}>
              <CheckCircleIcon className={styles.successIcon} />
              <div className={styles.successFullPaymentText}>
                The total amount for this order has been successfully paid.
              </div>
            </div>
          )}
        </div>
        <div className={styles.orderDetails}>
          <div className={styles.amountDetails}>
            <DetailDisplay
              title="Total Order Amount"
              value={INR_SYMBOL + reduceToFixedDecimals(totalAmount)}
            />
            <DetailDisplay
              title="Total Amount Paid"
              value={INR_SYMBOL + reduceToFixedDecimals(amountPaid)}
            />
            <DetailDisplay
              title="Total Pending Amount"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(pendingAmount))
              }
            />
            <DetailDisplay
              title="Amount Under Processing"
              value={
                INR_SYMBOL +
                Math.abs(reduceToFixedDecimals(paymentUnderProcessing))
              }
            />
            <DetailDisplay
              title="Excess Amount Paid"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(excessAmountPaid))
              }
            />
          </div>
          <DisplayOrder
            selectedPackageSubscriptions={selectedOrderDetails.packages}
          />
          <div
            className={styles.addNote}
            onClick={() => handleOnClickAddNote(setOpenDialogContent)}
          >
            Add a Note?
            <AddCircleIcon />
          </div>
          {selectedOrderDetails && (
            <PaymentDetails
              paymentDetails={selectedOrderDetails.payment_details}
            />
          )}
        </div>
      </div>
      <Dialog open={openDialogContent !== null} width="30rem" height="21rem">
        <AddNote
          onClose={handleDialogClose(setOpenDialogContent)}
          orderId={orderId}
        />
      </Dialog>
    </div>
  );
};

export default PaymentPreference;
